
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Eye, Clock } from 'lucide-react';
import { Topic, YorubaReaction } from '../types';
import YorubaReactions from '../YorubaReactions';
import { formatDistanceToNow } from 'date-fns';

interface TopicsListProps {
  topics: Topic[];
  loading: boolean;
  onReactionUpdate?: (topicId: string, reactions: YorubaReaction[]) => void;
}

export function TopicsList({ topics, loading, onReactionUpdate }: TopicsListProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse bg-white p-4 rounded-lg border">
            <div className="h-6 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-3/4 mb-3"></div>
            <div className="flex gap-4">
              <div className="h-4 bg-gray-100 rounded w-20"></div>
              <div className="h-4 bg-gray-100 rounded w-20"></div>
              <div className="h-4 bg-gray-100 rounded w-20"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (topics.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">No topics yet</p>
        <p className="text-gray-400">Be the first to start a discussion!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {topics.map((topic) => (
        <div key={topic.id} className="bg-white rounded-lg border hover:shadow-md transition-shadow">
          <Link to={`/community/hub/${topic.id}`} className="block p-4">
            <div className="flex items-start gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={topic.author.avatar || undefined} />
                <AvatarFallback className="bg-omoduduwa-terracotta text-white">
                  {topic.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-lg hover:text-omoduduwa-terracotta transition-colors line-clamp-1">
                    {topic.title}
                  </h3>
                  <Badge variant="secondary" className="ml-2">
                    {topic.category}
                  </Badge>
                </div>
                
                <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                  {topic.content}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500 gap-4">
                    <span className="font-medium">{topic.author.name}</span>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDistanceToNow(new Date(topic.created_at), { addSuffix: true })}
                    </div>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 gap-4">
                    <div className="flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      {topic.reply_count}
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {topic.view_count}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
          
          {/* Yoruba Reactions */}
          <div className="px-4 pb-4">
            <YorubaReactions
              targetType="topic"
              targetId={topic.id}
              reactions={topic.reactions || []}
              onReactionUpdate={(reactions) => onReactionUpdate?.(topic.id, reactions)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
